import React, { useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'
import {Controller, useForm} from 'react-hook-form'
import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid
} from '@material-ui/core'
import { styled } from '@mui/material/styles'
import { Close } from '@material-ui/icons'
import { Editor, EditorTools } from '@progress/kendo-react-editor'
import axios from 'axios'
import dayjs, {Dayjs} from 'dayjs'
import imageCompression from 'browser-image-compression'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import HookForm from '../../components/common/HookForm'
import CheckboxGroup from '../../components/common/inputs/CheckboxGroup'
import DateTimeInput from '../../components/common/inputs/DateTimeInput'
import SelectInput from '../../components/common/inputs/SelectInput'
import TextInput from '../../components/common/inputs/TextInput'
import { useAuthToken } from '../../slices/authSlice'
import { EvtState, setEvt } from '../../slices/evtSlice'
import { OrgState, setOrg, useOrgState } from '../../slices/orgSlice'
import { useFetchOrgSettings } from '../OrgSettingsPage/useFetchOrgSettings'
import { checkBidEndDate, checkBidStartDate, checkEventEndDate } from '../../util/formValidation'
import { markAuctionSettingsSaved } from '../../util/auctionSetup'
import { useSaveAuctionEventSettings } from './useSaveAuctionEventSettings'
import styles from './AuctionEventSettingsForm.module.css'
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";

type FormData = {
  name: string
  code: string
  actual: Dayjs;
  start: string
  bidstart: string
  bidend: string
  end: string
  timezone: string
  eventspaymentprocessorkey: string
  ccrequiredid: number
  ccfee: number
  eventbccemail: string
  startbiddernum: number
  textprefix: string
  storebuttonevent: string
  storebuttonauction: string
  storebuttondonate: string
  logo: string
  logourl: string
  logoremoved: boolean
  checks: {
    allowbillme: boolean
    allowpaymentoffees: boolean
    defaultpaymentoffees: boolean
    allowbidanonymously: boolean
    allowguestentry: boolean
    eventusepaddlenumber: boolean
    approvalofbidders: boolean
    allowcustomlocations: boolean
    eventshowbidderpaddlenumber: boolean
    allowattendedbytickettype: boolean
    allowtexting: boolean
    storebuttoneventshow: boolean
    storebuttonauctionshow: boolean
    storebuttondonateshow: boolean
  }
}

const richTextTools = [
  [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline, EditorTools.Strikethrough],
  EditorTools.ForeColor,
  EditorTools.BackColor,
  [EditorTools.CleanFormatting],
  [EditorTools.AlignLeft, EditorTools.AlignCenter, EditorTools.AlignRight, EditorTools.AlignJustify],
  [EditorTools.Indent, EditorTools.Outdent],
  [EditorTools.OrderedList, EditorTools.UnorderedList],
  EditorTools.FontSize,
  EditorTools.FontName,
  EditorTools.FormatBlock,
  [EditorTools.SelectAll],
  [EditorTools.Undo, EditorTools.Redo],
  [EditorTools.Link, EditorTools.Unlink, EditorTools.InsertImage, EditorTools.ViewHtml],
  [EditorTools.InsertTable, EditorTools.InsertFile],
  [EditorTools.AddRowBefore, EditorTools.AddRowAfter, EditorTools.AddColumnBefore, EditorTools.AddColumnAfter],
  [EditorTools.DeleteRow, EditorTools.DeleteColumn, EditorTools.DeleteTable],
  [EditorTools.MergeCells, EditorTools.SplitCell],
]

const FloatingButton = styled(Button)<ButtonProps>(() => ({
  margin: 'auto',
  top: 'auto',
  bottom: 20,
  left: '25%',
  position: 'fixed',
  width: '50%'
}))

const formatDate = (value: string) => dayjs(value).format('YYYY-MM-DDTHH:mm:ss')

// Combined Event & Auction settings, shown when a customer with both subscriptions creates a new event
const AuctionEventSettingsForm = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const orgState = useOrgState()
  const authToken = useAuthToken()
  const { data: settings } = useFetchOrgSettings({ userid: authToken?.userid })
  const saveMutation = useSaveAuctionEventSettings()

  const formMethods = useForm<FormData>()
  const { reset, watch, setValue } = formMethods
  const watchLogo = watch('logourl')
  const watchLogoData = watch('logo')
  const watchProcessorKey = watch('eventspaymentprocessorkey')
  const allowTextingWatch = watch('checks.allowtexting')
  const allowPaymentOfFeesWatch = watch('checks.allowpaymentoffees')
  const allowCustomLocations = watch('checks.allowcustomlocations')

  const [timezones, setTimezones] = useState([])
  const [paymentProcessors, setPaymentProcessors] = useState([])
  const [ccRequired, setCCRequired] = useState([])
  const [ccRequiredEnabled, setCCRequiredEnabled] = useState(false)
  const [privacyValue, setPrivacyValue] = useState('')
  const [cautionMsg, setCautionMsg] = useState('')
  const [failureMessage, setFailureMessage] = useState('')

  const twilioInvalid = orgState?.twilio?.valid === false

  useEffect(() => {
    if (settings) {
      const newOrgState: OrgState = {
        name: settings?.name, message: settings?.message, isdonorsnapcustomer: settings?.isdonorsnapcustomer, twilio: settings?.twilio
      }
      dispatch(setOrg(newOrgState))
    }
  }, [settings])

  useEffect(() => {
    const getDefaults = async () => {
      const { data } = await axios.get('/eventsadd')
      const defaults = JSON.parse(data.jsonString)

      setTimezones(defaults?.timezonelist?.map(tz => ({ value: tz.timezone, label: tz.timezone })) || [])
      setPaymentProcessors(defaults?.paymentprocessorlist?.map(p => ({ value: p.paymentprocessorkey, label: p.paymentprocessorname })) || [])
      setCCRequired(defaults?.ccrequiredlist?.map(cc => ({ value: cc.ccrequiredid, label: cc.ccrequiredname })) || [])
      setCCRequiredEnabled(!!defaults?.ccrequiredenabled)
      setPrivacyValue(defaults?.eventprivacy || '')

      const now = new Date().toISOString()
      reset({
        name: defaults?.name || '',
        code: defaults?.code || '',
        actual: defaults?.actual ? dayjs(defaults.actual) : dayjs(),
        start: defaults?.start ? defaults.start.split(' ').join('T') : now,
        bidstart: defaults?.bidstart ? defaults.bidstart.split(' ').join('T') : now,
        bidend: defaults?.bidend ? defaults.bidend.split(' ').join('T') : now,
        end: defaults?.end ? defaults.end.split(' ').join('T') : now,
        timezone: defaults?.timezone || 'Central Standard Time',
        eventspaymentprocessorkey: defaults?.eventspaymentprocessorkey || 'GEM0003',
        ccrequiredid: defaults?.ccrequiredid,
        ccfee: defaults?.ccfee ?? 2.5,
        eventbccemail: defaults?.eventbccemail || '',
        startbiddernum: defaults?.startbiddernum ?? 1,
        textprefix: defaults?.textprefix || '',
        storebuttonevent: defaults?.storebuttonevent || 'Register',
        storebuttonauction: defaults?.storebuttonauction || 'Auction',
        storebuttondonate: defaults?.storebuttondonate || 'Donate',
        logourl: defaults?.logourl || '',
        logoremoved: false,
        checks: {
          allowbillme: !!(defaults?.allowbillme ?? defaults?.billme),
          allowpaymentoffees: !!defaults?.allowpaymentoffees,
          defaultpaymentoffees: !!defaults?.defaultpaymentoffees,
          allowbidanonymously: defaults?.allowbidanonymously ?? true,
          allowguestentry: !!defaults?.allowguestentry,
          eventusepaddlenumber: !!defaults?.eventusepaddlenumber,
          approvalofbidders: !!defaults?.approvalofbidders,
          allowcustomlocations: !!defaults?.allowcustomlocations,
          eventshowbidderpaddlenumber: !!defaults?.eventshowbidderpaddlenumber,
          allowattendedbytickettype: !!defaults?.allowattendedbytickettype,
          allowtexting: !!defaults?.allowtexting,
          storebuttoneventshow: defaults?.storebuttoneventshow ?? true,
          storebuttonauctionshow: defaults?.storebuttonauctionshow ?? true,
          storebuttondonateshow: !!defaults?.storebuttondonateshow,
        }
      })
    }
    getDefaults()
  }, [])

  const handleCheckChange = (e, field) => {
    if (field.name === 'checks.approvalofbidders' && field.value === false) {
      setCautionMsg('Caution: Checking this box means that you will need to regularly check the Participants page to manually approve any newly registered participants. Participants that are not approved will not be able to bid on any items. To automatically approve all newly registered participants, leave this setting unchecked.')
    }
    else if (field.name === 'checks.allowcustomlocations' && field.value === false) {
      setCautionMsg('Caution: Checking this box means that all bidding open/close times are controlled by the Location settings. Changing the bidding open/close times on this page will not affect any existing location close times. Only use this setting if you plan to close tables at different times.')
    }
  }

  const onLogoDrop = async (files: File[]) => {
    if (!files?.length) return
    const compressedFile = await imageCompression(files[0], { maxSize: 1, maxWidthOrHeight: 1920, useWebWorker: true } as any)
    const fileReader = new FileReader()
    fileReader.onload = (event) => {
      const data = event.target.result as string
      setValue('logo', data.split(';base64,')[1])
    }
    fileReader.readAsDataURL(compressedFile)
  }

  const clickRemoveLogo = () => {
    setValue('logourl', '')
    setValue('logoremoved', true)
  }

  // Same fields as the Auction Settings create (PUT eventsettings) plus the Event-only settings
  const onSubmit = (formData: FormData) => {
    const { checks, start, actual, bidstart, bidend, end, ...rest } = formData

    saveMutation.mutate({
      ...rest,
      actual: dayjs(actual).format('YYYY-MM-DD'),
      start: formatDate(start),
      bidstart: formatDate(bidstart),
      bidend: formatDate(bidend),
      end: formatDate(end),
      ccfee: Number(formData.ccfee),
      startbiddernum: Number(formData.startbiddernum),
      ...checks,
      allowtexting: twilioInvalid ? false : checks.allowtexting,
      active: true,
      eventprivacy: privacyValue
    }, {
      onSuccess: (data) => {
        let json
        try {
          json = JSON.parse(data.data.jsonString)
        } catch {
          setFailureMessage('Unexpected response from the server. Please try again.')
          return
        }
        // Server may report "failure" or "failed"; anything short of success with an eventid is an error
        if (json?.status !== 'success' || !json?.eventid) {
          setFailureMessage(json?.message || 'Unable to save the event. Please try again.')
          return
        }
        const evtState: EvtState = {
          eventId: json.eventid,
          eventName: json.name,
          eventCode: json.code,
          allowpaymentoffees: json.allowpaymentoffees,
          billme: json.allowbillme ?? json.billme,
          allowattendedbytickettype: json.allowattendedbytickettype,
          ccfee: json.ccfee
        }
        dispatch(setEvt(evtState))
        // Auction settings were just saved here, so the Auction button can go straight to the dashboard
        markAuctionSettingsSaved(json.eventid)
        history.push('/event/' + json.eventid)
      },
      onError: (error: any) => {
        setFailureMessage(error?.response?.data?.message || error?.message || 'Unable to save the event. Please try again.')
      }
    })
  }

  return (
    <div className={styles.root}>
      <Dialog open={cautionMsg.length > 0}>
        <DialogContent>
          <DialogContentText>{cautionMsg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCautionMsg('')}>Ok</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failureMessage.length > 0}>
        <DialogContent>
          <DialogContentText><b>Failure:</b> {failureMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailureMessage('')}>Ok</Button>
        </DialogActions>
      </Dialog>

      <h1>Event &amp; Auction Settings</h1>
      <HookForm
        methods={formMethods}
        onSubmit={onSubmit}
        className={styles.form}
        formProps={{ id: 'auctionEventSettings-form' }}>

        <section className={styles.section}>
          <h2>General</h2>
          <TextInput name="name" label="Event/Auction Name" required tooltipText={'The name that will be displayed to your guests'} />
          <TextInput
            name="code"
            label="Event/Auction Code"
            required
            rules={{
              pattern: {
                value: /^[-_A-Za-z0-9]+$/,
                message: 'Event/Auction code can only contain letters, numbers, hyphens, underscores, and no spaces.'
              }
            }}
            tooltipText={'The part of the URL to specify your event. No spaces or special characters allowed. Keep it short and easy to remember.'}
          />
        </section>

        <section className={styles.section}>
          <h2>Dates &amp; Time</h2>
          <Grid container spacing={1}>
          <Grid item xs={6} className={styles.selectcontrol}>
            <Controller
                control={formMethods.control}
                name="actual"
                defaultValue={dayjs()}
                rules={{ required: true }}
                render={({ field }) => {
                  return (
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Date"
                            slotProps={{ textField: { size: 'small' } }}
                            value={field.value}
                            inputRef={field.ref}
                            onChange={(date) => {
                              field.onChange(date);
                            }}
                        />
                      </LocalizationProvider>

                  );
                }}
            />

          </Grid>

            <Grid item xs={6} className={styles.selectcontrol}>
              <SelectInput
                name="timezone"
                label={'Event/Auction Timezone'}
                options={timezones}
                includeNone={false}
                tooltipText={'The timezone where your event will be held. Dates/times will automatically adjust for each user’s location on their device.'}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={3} className={styles.selectcontrol}>
              <DateTimeInput
                name="start"
                label="Auction Open"
                defaultValue={null}
                required
                tooltipText={'The time when a user can log in to your event and see the auction items'}
              />
            </Grid>
            <Grid item xs={3} className={styles.selectcontrol}>
              {allowCustomLocations ? (
                <TextInput name="bidstartBlank" label="Bidding Open" defaultValue="Custom by Location" disabled />
              ) : (
                <DateTimeInput
                  name="bidstart"
                  label="Bidding Open"
                  rules={{ validate: (val) => checkBidStartDate(val, watch) }}
                  defaultValue={new Date().toISOString()}
                  required
                  tooltipText={'The time when a user can begin bidding on auction items'}
                />
              )}
            </Grid>
            <Grid item xs={3} className={styles.selectcontrol}>
              {allowCustomLocations ? (
                <TextInput name="bidendBlank" label="Bidding Close" defaultValue="Custom by Location" disabled />
              ) : (
                <DateTimeInput
                  name="bidend"
                  label="Bidding Close"
                  rules={{ validate: (val) => checkBidEndDate(val, watch) }}
                  defaultValue={new Date().toISOString()}
                  required
                />
              )}
            </Grid>
            <Grid item xs={3} className={styles.selectcontrol}>
              <DateTimeInput
                name="end"
                label="Auction Close"
                rules={{ validate: (val) => checkEventEndDate(val, watch) }}
                defaultValue={new Date().toISOString()}
                required
                tooltipText={'The time when the auction website and checkout pages will no longer be accessible. This is usually multiple days after bidding closes to allow collecting payment at a later date.'}
              />
            </Grid>
          </Grid>
        </section>

        <section className={styles.section}>
          <h2>Payments</h2>
          <Grid container spacing={1}>
            <Grid item xs={6} className={styles.selectcontrol}>
              <SelectInput
                name="eventspaymentprocessorkey"
                label={'Payment Processor'}
                options={paymentProcessors}
                includeNone={false}
              />
            </Grid>
            <Grid item xs={6} className={styles.selectcontrol}>
              <SelectInput
                name="ccrequiredid"
                label={'Require Credit/Debit Card on File (Stripe Only)'}
                options={ccRequired}
                disabled={!ccRequiredEnabled || !watchProcessorKey?.startsWith('STRIPE')}
                includeNone={false}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={6} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                options={[
                  {
                    value: 'allowbillme',
                    label: 'Allow Bill Me',
                    tooltipText: 'Allow checkout without immediate payment in order to send an invoice at a later date.'
                  }
                ]}
              />
              <TextInput
                name="eventbccemail"
                label="Purchase Notification Email"
                tooltipText={'Email address to receive a BCC on all purchase notifications sent to guests'}
                textFieldProps={{ placeholder: 'email@domain.org' }}
                rules={{
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address'
                  }
                }}
              />
            </Grid>
            <Grid item xs={6} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                options={[
                  {
                    value: 'allowpaymentoffees',
                    label: 'Allow Payment of CC Fees',
                    tooltipText: 'Display a checkbox during checkout for the user to add a set percentage to their payment to offset payment processing fees.',
                    textoptions: {
                      name: 'ccfee',
                      label: 'CC Fee % to Add',
                      defaultValue: '2.5',
                      endAdornment: '%',
                      required: allowPaymentOfFeesWatch,
                      tooltipText: 'If the user opts to cover the payment processing fees, this percentage will be added to their payment total.'
                    },
                    checkboptions: {
                      value: 'defaultpaymentoffees',
                      label: 'Default to Include CC Fees',
                      tooltipText: 'When enabled, the CC fee will be checked by default at checkout.'
                    }
                  }
                ]}
              />
            </Grid>
          </Grid>
        </section>

        <section className={styles.section}>
          <h2>Bidders &amp; Participants</h2>
          <Grid container spacing={1}>
            <Grid item xs={4} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                onChange={handleCheckChange}
                options={[
                  { value: 'allowbidanonymously', label: 'Allow Bid Anonymously', tooltipText: 'Give users the option to hide their name in the Bid History section.' },
                  { value: 'allowguestentry', label: 'Allow Guest Entry (View Only)', tooltipText: 'Allow users to browse your auction items without logging in. All users must log in to place a bid.' },
                  { value: 'eventusepaddlenumber', label: 'Use manually assigned paddle number instead of auto-assigned bidder number', tooltipText: '' },
                ]}
              />
            </Grid>
            <Grid item xs={4} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                onChange={handleCheckChange}
                options={[
                  { value: 'approvalofbidders', label: 'Require Approval of Bidders', tooltipText: 'Do not allow bidding by users that have not been manually approved in the Participants page.' },
                  { value: 'allowcustomlocations', label: 'Allow custom bidding open/close by location', tooltipText: 'Each location will have independent open and close times for bidding. Set up these times on the Locations page.' },
                ]}
              />
            </Grid>
            <Grid item xs={4} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                options={[
                  { value: 'eventshowbidderpaddlenumber', label: 'Show Bidder/Paddle Number to Participant', tooltipText: '' },
                  { value: 'allowattendedbytickettype', label: 'Track Attended per Ticket', tooltipText: '' },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={4} className={styles.selectcontrol}>
              <TextInput
                name="startbiddernum"
                label="Starting Bidder Number"
                defaultValue="1"
                required
                tooltipText={'The first bidder number that will be assigned to a participant followed by sequential order.'}
              />
            </Grid>
          </Grid>
        </section>

        <section className={styles.section}>
          <h2>Texting</h2>
          <CheckboxGroup
            name="checks"
            options={[
              {
                value: 'allowtexting',
                label: 'Allow Texting',
                tooltipText: 'Send text message notifications to the users that have enabled them.',
                disabled: twilioInvalid,
                textoptions: {
                  name: 'textprefix',
                  label: 'Text Prefix',
                  defaultValue: '',
                  disabled: !allowTextingWatch || twilioInvalid,
                  maxLength: 50,
                  tooltipText: 'Add an optional prefix to all text message notifications to help identify your organization. Up to 50 characters are allowed. A colon and space are added automatically.'
                }
              }
            ]}
          />
        </section>

        <section className={styles.section}>
          <h2>Landing Page Buttons</h2>
          <Grid container spacing={1}>
            <Grid item xs={4} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                options={[{
                  value: 'storebuttoneventshow',
                  label: 'Show Event Button',
                  textoptions: { name: 'storebuttonevent', label: 'Event Button Text', defaultValue: 'Register', maxLength: 50 }
                }]}
              />
            </Grid>
            <Grid item xs={4} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                options={[{
                  value: 'storebuttonauctionshow',
                  label: 'Show Auction Button',
                  textoptions: { name: 'storebuttonauction', label: 'Auction Button Text', defaultValue: 'Auction', maxLength: 50 }
                }]}
              />
            </Grid>
            <Grid item xs={4} className={styles.selectcontrol}>
              <CheckboxGroup
                name="checks"
                options={[{
                  value: 'storebuttondonateshow',
                  label: 'Show Donate Button',
                  textoptions: { name: 'storebuttondonate', label: 'Donate Button Text', defaultValue: 'Donate', maxLength: 50 }
                }]}
              />
            </Grid>
          </Grid>
        </section>

        <section className={styles.section}>
          <h2>Event/Auction Image</h2>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
            {watchLogo && (
              <div>
                <div><Close fontSize="small" onClick={clickRemoveLogo} /></div>
                <div className={styles.logoWrapper}>
                  <img src={watchLogo} alt="logo" />
                </div>
              </div>
            )}
            {watchLogoData && (
              <div>
                <div style={{ paddingBottom: '2px' }}>New Event/Auction Image</div>
                <div className={styles.logoWrapper}>
                  <img src={`data:image/png;base64,${watchLogoData}`} alt="logo" />
                </div>
              </div>
            )}
            <Dropzone onDrop={onLogoDrop} accept={[
              'image/jpeg', 'image/jpg', 'image/*', 'image/x-png', 'image/bmp', 'image/gif', 'image/x-ms-bmp', 'image/x-bmp']}>
              {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }) => (
                <section>
                  <div {...getRootProps()} className={isDragActive ? (isDragAccept ? styles.dropzoneselect : styles.dropzoneinvalid) : styles.dropzone}>
                    <input {...getInputProps()} />
                    {isDragAccept && (<h4>File is a valid image type. Go ahead and drop.</h4>)}
                    {!isDragActive && (<h4>Drag 'n' drop files here, or click to select a file</h4>)}
                    {isDragReject && (<h4>File cannot be dropped. It's an invalid type.</h4>)}
                    <h5>Images display at a max height of 450px. Only .jpg, .jpeg, .png, .gif, and .bmp images under 5MB are allowed.</h5>
                  </div>
                </section>
              )}
            </Dropzone>
          </div>
        </section>

        <section className={styles.section}>
          <h2>Event/Auction Privacy/Refund Policy</h2>
          <h5>This content will show above the login fields while the event is open.</h5>
          <Editor
            tools={richTextTools}
            contentStyle={{ height: 230 }}
            value={privacyValue}
            onChange={(e) => setPrivacyValue(e.html)}
          />
        </section>

        <FloatingButton type="submit" variant="contained" className={styles.button}>
          Save
        </FloatingButton>
      </HookForm>
    </div>
  )
}

export default AuctionEventSettingsForm
